import React, { createContext, useContext, useReducer } from 'react';

export type WallSide = 'top' | 'right' | 'bottom' | 'left';

export interface EntrancePosition {
    wall: WallSide;
    position: number; // normalized 0..1 along the wall
}

export type TableShape = 'round' | 'square' | 'rectangle';

export interface EditorTable {
    id: string;
    label: string;
    shape: TableShape;
    seats: number;
    qrCode: string | null;
    x: number; // 0..1 center within the room
    y: number; // 0..1 center within the room
    width: number; // 0..1 of room width
    height: number; // 0..1 of room height
    isActive: boolean;
}

export interface TableMapDraft {
    name: string;
    isActive: boolean;
    gridEnabled: boolean;
    entrance: EntrancePosition | null;
    tables: EditorTable[];
}

type WizardAction =
    | { type: 'SET_NAME'; payload: string }
    | { type: 'SET_IS_ACTIVE'; payload: boolean }
    | { type: 'SET_GRID_ENABLED'; payload: boolean }
    | { type: 'SET_ENTRANCE'; payload: EntrancePosition | null }
    | { type: 'ADD_TABLE'; payload: Omit<EditorTable, 'qrCode'> }
    | { type: 'UPDATE_TABLE_POSITION'; payload: { id: string; x: number; y: number } }
    | { type: 'UPDATE_TABLE'; payload: { id: string } & Partial<Omit<EditorTable, 'id'>> }
    | { type: 'REMOVE_TABLE'; payload: { id: string } };

interface WizardContextValue {
    draft: TableMapDraft;
    dispatch: React.Dispatch<WizardAction>;
}

const defaultDraft: TableMapDraft = {
    name: '',
    isActive: true,
    gridEnabled: true,
    entrance: null,
    tables: [],
};

function wizardReducer(state: TableMapDraft, action: WizardAction): TableMapDraft {
    switch (action.type) {
        case 'SET_NAME':
            return { ...state, name: action.payload };
        case 'SET_IS_ACTIVE':
            return { ...state, isActive: action.payload };
        case 'SET_GRID_ENABLED':
            return { ...state, gridEnabled: action.payload };
        case 'SET_ENTRANCE':
            return { ...state, entrance: action.payload };
        case 'ADD_TABLE':
            return {
                ...state,
                tables: [...state.tables, { ...action.payload, qrCode: null, isActive: true }],
            };
        case 'UPDATE_TABLE_POSITION':
            return {
                ...state,
                tables: state.tables.map((t) =>
                    t.id === action.payload.id
                        ? { ...t, x: action.payload.x, y: action.payload.y }
                        : t
                ),
            };
        case 'UPDATE_TABLE':
            return {
                ...state,
                tables: state.tables.map((t) =>
                    t.id === action.payload.id ? { ...t, ...action.payload } : t
                ),
            };
        case 'REMOVE_TABLE':
            return {
                ...state,
                tables: state.tables.filter((t) => t.id !== action.payload.id),
            };
        default:
            return state;
    }
}

const TableMapWizardContext = createContext<WizardContextValue | undefined>(undefined);

interface TableMapWizardProviderProps {
    children: React.ReactNode;
    initialDraft?: TableMapDraft;
}

export function TableMapWizardProvider({ children, initialDraft }: TableMapWizardProviderProps) {
    const [draft, dispatch] = useReducer(wizardReducer, initialDraft ?? defaultDraft);
    return (
        <TableMapWizardContext.Provider value={{ draft, dispatch }}>
            {children}
        </TableMapWizardContext.Provider>
    );
}

export function useTableMapWizard(): WizardContextValue {
    const ctx = useContext(TableMapWizardContext);
    if (!ctx) {
        throw new Error('useTableMapWizard must be used inside TableMapWizardProvider');
    }
    return ctx;
}

export type { WizardAction };
